import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useRef, useState } from 'react';
import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import { agent } from 'services/service';
import { VehicleSearchResponse, VehicleSearchRow } from 'services/VehicleService';
import {
  AttendanceDashboardPeriod,
  formatDashboardPeriodQuery
} from 'utils/attendanceDashboardPeriod';
import {
  ATTENDANCE_STATUS_COLUMN_LABELS,
  getAttendanceStatusClass
} from 'utils/formatAttendanceDashboard';
import './index.scss';

const SEARCH_DEBOUNCE_MS = 300;

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

const renderStatus = (row: VehicleSearchRow) =>
  row.status ? (
    <span
      className={`vehicle-search__status vehicle-search__status--${getAttendanceStatusClass(
        row.status
      )}`}
    >
      {ATTENDANCE_STATUS_COLUMN_LABELS[row.status] ?? row.status}
    </span>
  ) : null;

export const VehicleSearch = () => {
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<AttendanceDashboardPeriod | null>(null);
  const [result, setResult] = useState<VehicleSearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const query = period ? formatDashboardPeriodQuery(period) : null;
  const from = query?.from;
  const to = query?.to;
  const showAttendance = !!period;
  const showDateColumn = showAttendance && from !== to;

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await agent.VehicleService.search(search, from, to);
        if (requestId === requestIdRef.current) setResult(response.data);
      } catch (err: any) {
        if (requestId === requestIdRef.current) {
          setResult(null);
          setError(
            err?.response?.data?.message ?? 'Could not search vehicles.'
          );
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [search, from, to]);

  const items = result?.items ?? [];
  const term = search.trim();
  const hasSearch = term.length > 0;

  const noVehicleMessage = hasSearch
    ? `No vehicle matches "${term}".`
    : 'No vehicles have been registered yet.';

  const formatMeta = () => {
    if (!result) return null;

    if (!showAttendance) {
      return hasSearch
        ? `${plural(result.vehicleCount, 'vehicle')} matching "${term}"`
        : `All registered vehicles (${result.vehicleCount})`;
    }

    const records = plural(items.length, 'attendance record');
    return hasSearch
      ? `${records} for "${term}" · ${result.dateRangeLabel}`
      : `${records} · ${result.dateRangeLabel}`;
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="vehicle-search__message">
          <CircularProgress size={24} />
          <span>Searching...</span>
        </div>
      );
    }

    if (error) {
      return <div className="vehicle-search__message">{error}</div>;
    }

    if (!result || result.vehicleCount === 0) {
      return <div className="vehicle-search__message">{noVehicleMessage}</div>;
    }

    if (items.length === 0) {
      return (
        <div className="vehicle-search__message">
          {hasSearch
            ? `No attendance records for "${term}" on ${result.dateRangeLabel}.`
            : `No attendance records for vehicle owners on ${result.dateRangeLabel}.`}
        </div>
      );
    }

    return (
      <table className="vehicle-search__table">
        <thead>
          <tr>
            {showDateColumn && <th>Date</th>}
            <th>Vehicle No.</th>
            <th>Employee</th>
            <th>ID</th>
            {showAttendance && (
              <>
                <th>Arrival</th>
                <th>Departure</th>
                <th>Status</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={`${row.vehicleId}-${row.date ?? 'any'}`}>
              {showDateColumn && (
                <td className="vehicle-search__date-cell">{row.dayLabel}</td>
              )}
              <td className="vehicle-search__number">{row.vehicleNumber}</td>
              <td>{row.employeeName}</td>
              <td>{row.epfNumber}</td>
              {showAttendance && (
                <>
                  <td>{row.arrival ?? '—'}</td>
                  <td>{row.departure ?? '—'}</td>
                  <td>{renderStatus(row)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const meta = !loading && !error && items.length > 0 ? formatMeta() : null;

  return (
    <div className="vehicle-search-page">
      <AttendanceTrackingPageHeader
        title="Vehicles"
        description="Search by vehicle number to find the owner. Pick a date or range to see their attendance."
        crumbs={[{ label: 'Attendance Tracking' }, { label: 'Vehicles' }]}
      />

      <div className="vehicle-search">
        <div className="vehicle-search__toolbar">
          <label className="vehicle-search__search">
            <SearchOutlinedIcon fontSize="small" aria-hidden />
            <input
              type="search"
              value={search}
              autoFocus
              placeholder="Search vehicle number, e.g. CAB 1234"
              aria-label="Vehicle number"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <AttendanceDashboardPeriodFilter
            className="vehicle-search__period"
            period={period}
            onChange={setPeriod}
            onClear={() => setPeriod(null)}
            includeDate
            maxDate={new Date()}
          />
        </div>

        <section className="vehicle-search__card">
          {meta && <div className="vehicle-search__meta">{meta}</div>}
          <div className="vehicle-search__table-wrap">{renderBody()}</div>
        </section>
      </div>
    </div>
  );
};
